import { FC, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import cx from 'classnames';
import EmptyBoxIcon from '@/storybook/icons/EmptyBoxIcon';
import FilterLinesIcon from '@/storybook/icons/FilterLinesIcon';
import Button from '@/storybook/components/Button/Button';
import Popover from '@/storybook/components/Popover/Popover';
import { UserFilterListItem } from '@/models/user/userFilter';
import { AppliedFilterSetStorage, APPLIED_FILTER_SET_KEY } from '@/utils/filter/appliedFilterSetStorage';
import { SESSION_STORAGE_SYNC_EVENT } from '@/hooks/useSessionStorage';

import styles from './SavedFiltersDropdown.module.css';

const TEST_ID = 'saved-filters-dropdown';

const emptyStateTitleComponents = {
  bold: <strong className={styles.emptyStateTitleEmphasis} />,
};

interface SavedFiltersDropdownProps {
  filters?: UserFilterListItem[] | null;
  onApplyFilter?: (filterId: string) => void;
  onViewAll?: () => void;
}

const SavedFiltersDropdown: FC<SavedFiltersDropdownProps> = ({ filters, onApplyFilter, onViewAll }) => {
  const { t } = useTranslation();
  const savedFilters = filters ?? [];
  const hasSavedFilters = savedFilters?.length > 0;
  const [appliedFilterId, setAppliedFilterId] = useState<string | null>(
    () => AppliedFilterSetStorage.get()?.userFilterId ?? null,
  );

  const handleStorageSync = useCallback((event: Event) => {
    if (event instanceof CustomEvent && event.detail?.key === APPLIED_FILTER_SET_KEY) {
      setAppliedFilterId(AppliedFilterSetStorage.get()?.userFilterId ?? null);
    }
  }, []);

  useEffect(() => {
    window.addEventListener(SESSION_STORAGE_SYNC_EVENT, handleStorageSync);
    return () => window.removeEventListener(SESSION_STORAGE_SYNC_EVENT, handleStorageSync);
  }, [handleStorageSync]);

  const dropdownContent = (
    <div className={styles.dropdownPanel} data-testid={`${TEST_ID}-panel`}>
      <span className={styles.heading}>{t('global.filters.savedFiltersDropdown.recentlySavedFilters')}</span>

      <ul className={styles.filterList}>
        {hasSavedFilters ? (
          savedFilters.map(savedFilter => {
            const isApplied = savedFilter.userFilterId === appliedFilterId;
            return (
              <li key={savedFilter.userFilterId} className={styles.filterItem}>
                <button
                  type="button"
                  className={cx(styles.filterButton, isApplied && styles.filterButtonApplied)}
                  data-testid={`${TEST_ID}-item-${savedFilter.userFilterId}`}
                  onClick={() => onApplyFilter?.(savedFilter.userFilterId)}
                  disabled={isApplied}
                  aria-current={isApplied ? 'true' : undefined}
                >
                  <span className={styles.filterName}>{savedFilter.filterName ?? savedFilter.userFilterId}</span>
                </button>
              </li>
            );
          })
        ) : (
          <li className={styles.emptyState} data-testid={`${TEST_ID}-empty-state`}>
            <EmptyBoxIcon className={styles.emptyStateIcon} aria-hidden="true" />
            <span className={styles.emptyStateTitle}>
              <Trans
                i18nKey="global.filters.savedFiltersDropdown.emptyStateTitle"
                components={emptyStateTitleComponents}
              />
            </span>
            <span className={styles.emptyStateDescription}>
              {t('global.filters.savedFiltersDropdown.emptyStateDescription')}
            </span>
          </li>
        )}
      </ul>

      {hasSavedFilters ? (
        <Button
          variant="text"
          color="default"
          size="M"
          startIcon={<FilterLinesIcon />}
          onClick={onViewAll}
          data-testid={`${TEST_ID}-view-all`}
          className={styles.viewAllButton}
        >
          {t('global.filters.savedFiltersDropdown.viewAllSavedFilters')}
        </Button>
      ) : (
        <div className={styles.placeholder} />
      )}
    </div>
  );

  return (
    <Popover
      size="S"
      noticePosition="bottom-right"
      verticalOffset={-7}
      raw
      withCloseButton={false}
      closeOnNoticeContentClick
      data-testid={TEST_ID}
      anchorContent={
        <Button
          iconOnly
          variant="outlined"
          size="L"
          icon={<FilterLinesIcon />}
          data-testid={`${TEST_ID}-trigger`}
          aria-label={t('global.filters.savedFiltersDropdown.recentlySavedFilters')}
        />
      }
      noticeContent={dropdownContent}
    />
  );
};

export default SavedFiltersDropdown;
